import React, { useState, useEffect, useRef } from "react";
import { Plug, MessageSquare, Loader2 } from "lucide-react";
import { AppConfig } from "../../utils/Constants";
import { hubSpotService } from "../../services/HubSpotService";
import { useToastContext } from "../../context/ToastContext";
import { useLoader } from "../../context/LoaderContext";
import ConfirmDisconnectModal from "./ConfirmDisconnectModal";
import { useNavigate, useLocation } from "react-router-dom";

const HubSpotAgentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [_, setIsExpired] = useState<boolean>(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(true);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const [hubspotInstanceId, setHubspotInstanceId] = useState<string | null>(
    null
  );

  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple checks if already checked
    if (hasCheckedRef.current) {
      return;
    }

    let isMounted = true;

    const checkStatus = async () => {
      hasCheckedRef.current = true;

      showLoader();
      try {
        const { data } = await hubSpotService.getHubSpotStatus();
        if (isMounted) {
          setIsConnected(data.connected);
          setIsExpired(data.isExpired || false);
          setHubspotInstanceId(data.hubspotInstanceId || null);

          if (data.connected && location.pathname !== "/hubspot/display") {
            // If connected, navigate directly to display page (which has its own loading screen)
            // Only navigate if we're not already on the display page
            hideLoader();
            navigate("/hubspot/display", { replace: true });
            return;
          }

          setIsLoadingStatus(false);
        }
      } catch (error: any) {
        console.error("Error checking HubSpot status:", error);
        if (isMounted) {
          setIsLoadingStatus(false);
        }
        hasCheckedRef.current = false; // Reset on error
      } finally {
        if (isMounted) {
          hideLoader();
        }
      }
    };

    checkStatus();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-check if pathname changes

  const handleConnect = async () => {
    showLoader();
    try {
      const { data, message } = await hubSpotService.connectHubSpot();
      if (data.authorizationUrl) {
        window.location.href = data.authorizationUrl;
      }
      if (message) console.log("Message:", message);
    } catch (error: any) {
      console.error("Error connecting HubSpot:", error);
      showToast("error", error.message || "Failed to connect HubSpot.");
    } finally {
      hideLoader();
    }
  };

  const handleDisconnect = async () => {
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    setShowDisconnectModal(false);
    showLoader();
    try {
      await hubSpotService.disconnectHubSpot();
      setIsConnected(false);
      showToast("success", "HubSpot disconnected successfully.");
    } catch (error: any) {
      showToast("error", "Failed to disconnect HubSpot.");
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark relative overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="subtle-grid"></div>

      <main className="flex-1 flex flex-col items-center justify-start px-6 pt-8 pb-16 relative z-10">
        <div className="max-w-2xl mx-auto w-full text-center mt-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-secondary/10 dark:bg-secondary/10 rounded-xl">
              <MessageSquare className="w-8 h-8 text-secondary" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-semibold text-black dark:text-text-primaryDark mb-3 tracking-tight">
            HubSpot Analytics Agent
          </h1>
          
          {/* Description */}
          <p className="text-base text-black/60 dark:text-text-secondaryDark mb-8 max-w-xl mx-auto leading-relaxed">
            Connect your HubSpot account to analyze your data, view metrics, and chat with AI to uncover insights.
          </p>

          {/* Connect Button */}
          {isLoadingStatus ? (
            <div className="flex items-center justify-center gap-2 text-black/60 dark:text-text-secondaryDark">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Checking connection...</span>
            </div>
          ) : !isConnected ? (
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={handleConnect}
                className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-sm font-semibold rounded-lg hover:bg-black/90 dark:hover:bg-white/90 transition-colors"
              >
                Connect HubSpot
              </button>
              <p className="text-xs text-black/50 dark:text-text-secondaryDark">
                You'll be redirected to HubSpot to authorize the connection
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                <div className="w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full"></div>
                <span className="text-sm font-medium">Connected</span>
              </div>
              <button
                onClick={() => navigate("/hubspot/display")}
                className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-sm font-semibold rounded-lg hover:bg-black/90 dark:hover:bg-white/90 transition-colors"
              >
                View Dashboard
              </button>
              <button
                onClick={handleDisconnect}
                className="text-xs text-black/50 dark:text-text-secondaryDark hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                Disconnect
              </button>
            </div>
          )}

          {/* Features - Simple list */}
          {!isConnected && (
            <div className="mt-16 pt-12 border-t border-black/8 dark:border-divider-dark">
              <h2 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-6">
                What you'll get
              </h2>
              <div className="space-y-4 text-left max-w-md mx-auto">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-secondary/10 dark:bg-secondary/10 rounded text-secondary flex-shrink-0 mt-0.5">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-black dark:text-text-primaryDark mb-1">
                      AI-Powered Chat
                    </h3>
                    <p className="text-xs text-black/60 dark:text-text-secondaryDark">
                      Ask questions about your HubSpot data and get instant insights
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-secondary/10 dark:bg-secondary/10 rounded text-secondary flex-shrink-0 mt-0.5">
                    <Plug className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-black dark:text-text-primaryDark mb-1">
                      Real-Time Metrics
                    </h3>
                    <p className="text-xs text-black/60 dark:text-text-secondaryDark">
                      View deals, pipelines, contacts, and companies at a glance
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-secondary/10 dark:bg-secondary/10 rounded text-secondary flex-shrink-0 mt-0.5">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-black dark:text-text-primaryDark mb-1">
                      Data Analysis
                    </h3>
                    <p className="text-xs text-black/60 dark:text-text-secondaryDark">
                      Analyze trends, identify bottlenecks, and optimize your pipeline
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <ConfirmDisconnectModal
        open={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={confirmDisconnect}
        integration="hubspot"
      />
    </div>
  );
};

export default HubSpotAgentPage;
